import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Alert, Box, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from '../../../axios';
import { useAuth } from '../../../context/AuthContext';
import Swal from 'sweetalert2';
import PasswordTextField from '../../PasswordTextField';

export default function AccountEmployee() {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState({
    funeralhomename: '', 
    username: '', 
    email: '',
    download_count: '',
  });
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const { isLoggedIn, isTrial, isOwner} = useAuth();

  useEffect(() => {
    if (!isLoggedIn) {
        navigate('/login');
    } else {
      fetchUserData();
    }

    if(isOwner) {
      navigate("/account");
    }
}, []);

async function fetchUserData() {
    try {
        const res = await axios.get('/user');
          setAccountInfo(res.data[0]);
    } catch (error) {
         Swal.fire({
            title: 'Błąd!',
            text: 'Wystąpił błąd.',
            icon: 'warning',
            confirmButtonText: 'OK',
            background: '#fff',
            color: '#000',
            confirmButtonColor: '#d33'
        });
    }
}

async function handleSaving(accountInfo) {
  try {
    await axios.put('/updateUser',
      {
        username: accountInfo.username, 
        email: accountInfo.email,
      }
    );
    } catch (error) {
         Swal.fire({
            title: 'Błąd!',
            text: 'Wystąpił błąd.',
            icon: 'warning',
            confirmButtonText: 'OK',
            background: '#fff',
            color: '#000',
            confirmButtonColor: '#d33'
        });
    }
}

  const handlePasswordSave = async () => {
    if (newPassword !== confirmNewPassword) {
      alert('Nowe hasło i potwierdzenie hasła muszą być zgodne.');
      return;
    }
    if (newPassword==='' || confirmNewPassword===''||oldPassword=='') {
      alert('Uzupełnij wszystkie pola');
      return;
    }

    try {

      await axios.post('/changePassword', 
        {
          oldPassword: oldPassword,
          newPassword: newPassword
        }
      );
      alert('Hasło zostało zmienione.');
      setIsPasswordDialogOpen(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      Swal.fire({
        title: 'Błąd!',
        text: 'Wystąpił błąd.',
        icon: 'warning',
        confirmButtonText: 'OK',
        background: '#fff',
        color: '#000',
        confirmButtonColor: '#d33'
    });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountInfo({
      ...accountInfo,
      [name]: value
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    setIsEditing(false);
    handleSaving(accountInfo);
  };

  const handlePasswordChangeClick = () => {
    setIsPasswordDialogOpen(true);
  };
  

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Miesiące są indeksowane od 0
  const year = date.getFullYear();
  return `${day}.${month}.${year}r.`;
};

  const avatarInitial = accountInfo.email ? accountInfo.email.charAt(0).toUpperCase() : '?';

  const primaryButtonStyles = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    backgroundColor: '#5189B0',
    boxShadow: '0 12px 32px rgba(81, 137, 176, 0.25)',
    '&:hover': {
      backgroundColor: '#63b3be',
      boxShadow: '0 14px 34px rgba(99, 179, 190, 0.35)',
    },
  };

  const outlinedButtonStyles = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    borderColor: '#5189B0',
    color: '#5189B0',
    '&:hover': {
      borderColor: '#63b3be',
      backgroundColor: 'rgba(99, 179, 190, 0.08)',
      color: '#63b3be',
    },
  };

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(180deg, rgba(238,248,253,0.9) 0%, rgba(255,255,255,1) 100%)',
          minHeight: '100vh',
          py: { xs: 12, md: 16 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={4}>
            {isTrial && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 18px 40px rgba(81, 137, 176, 0.15)',
                  backgroundColor: 'rgba(81, 137, 176, 0.08)',
                  color: '#1D1D1F',
                }}
              >
                <Typography component="span" fontWeight={700}>
                  Korzystasz z okresu próbnego
                </Typography>{' '}
                - twoje wygenerowane elementy będą posiadały znak wodny.
              </Alert>
            )}

            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, boxShadow: '0 24px 60px rgba(81, 137, 176, 0.12)', backgroundColor: '#FFFFFF' }}>
                  <Stack spacing={2} alignItems="center" textAlign="center">
                    <Avatar sx={{ width: 96, height: 96, bgcolor: '#5189B0', fontSize: '2rem' }}>{avatarInitial}</Avatar>
                    <Typography variant="h5" fontWeight={700} color="#1D1D1F">
                      {accountInfo.email}
                    </Typography>
                  </Stack>

                  <Stack spacing={2} mt={4}>
                    <TextField
                      label="Nazwa zakładu pogrzebowego*"
                      name="funeralhomename"
                      value={accountInfo.funeralhomename}
                      fullWidth
                      disabled
                    />
                    <TextField
                      label="Nazwa"
                      name="username"
                      value={accountInfo.username}
                      onChange={handleInputChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                    <TextField
                      label="E-mail"
                      name="email"
                      value={accountInfo.email}
                      fullWidth
                      disabled
                    />
                    <TextField
                      label="Ilość pobrań"
                      name="download_count"
                      value={accountInfo.download_count}
                      fullWidth
                      disabled
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={4}>
                    <Button
                      variant="outlined"
                      onClick={handlePasswordChangeClick}
                      sx={{ ...outlinedButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                    >
                      Zmień hasło
                    </Button>

                    {!isEditing ? (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEditClick}
                        sx={{ ...primaryButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                      >
                        Edytuj
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveClick}
                        sx={{ ...primaryButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                      >
                        Zapisz zmiany
                      </Button>
                    )}
                  </Stack>

                  <Typography variant="body2" sx={{ mt: 3, color: '#4A5660' }}>
                    Data wygaśnięcia subskrybcji:{' '}
                    <Box component="span" sx={{ color: '#5189B0', fontWeight: 700 }}>
                      {formatDate(accountInfo.subscription_expiration_date)}
                    </Box>
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, boxShadow: '0 24px 60px rgba(81, 137, 176, 0.12)', backgroundColor: '#FFFFFF' }}>
                  <Typography variant="h6" fontWeight={700} color="#1D1D1F" textAlign="center">
                    Personalizuj informacje do generowania
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/massplaces" fullWidth variant="contained" sx={primaryButtonStyles}>
                        Edytuj miejsca mszy
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/funeralplaces" fullWidth variant="contained" sx={primaryButtonStyles}>
                        Edytuj miejsca pochówku
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/quotes" fullWidth variant="contained" sx={primaryButtonStyles}>
                        Edytuj cytaty
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/definitions" fullWidth variant="contained" sx={primaryButtonStyles}>
                        Edytuj definicje
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/familymembers" fullWidth variant="contained" sx={primaryButtonStyles}>
                        Edytuj członków rodziny
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/templates" fullWidth variant="contained" sx={primaryButtonStyles}>
                        Edytuj ramki
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>

      <Dialog open={isPasswordDialogOpen} onClose={() => setIsPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" color="#1D1D1F" fontWeight={700}>
            Wprowadź nowe hasło.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <PasswordTextField
            autoFocus
            margin="dense"
            label="Stare hasło"
            fullWidth
            required
            variant="standard"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <PasswordTextField
            margin="dense"
            label="Nowe hasło"
            required
            fullWidth
            variant="standard"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <PasswordTextField
            margin="dense"
            label="Potwierdź nowe hasło"
            fullWidth
            required
            variant="standard"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPasswordDialogOpen(false)} color="primary">
            Anuluj
          </Button>
          <Button onClick={handlePasswordSave} color="primary">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
